import { safeShare } from '@repo/shared/marketing';

const COUNT = new Intl.NumberFormat('en-US');
const USD = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

/** Grouped whole number ("1,240"). */
export function formatCount(value: number): string {
  return COUNT.format(value);
}

/** Whole cents → grouped whole dollars ("$2,940"). Attributed revenue is always an exact figure. */
export function formatUsd(cents: number): string {
  return USD.format(Math.round(cents / 100));
}

/** `formatUsd`, or "—" where the API sends no amount (the salon's setup values no visit). */
export function formatUsdOrDash(cents: number | null): string {
  return cents === null ? '—' : formatUsd(cents);
}

/** Whole cents → dollars-and-cents ("$42.00") — the per-appointment revenue in the Leads drawer. */
export function formatMoneyCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/** "live 6 days" / "live 1 day" / "not live yet" — from the server-computed inclusive live-day count. */
export function liveDaysLabel(liveDays: number): string {
  if (liveDays <= 0) return 'not live yet';
  return `live ${liveDays} day${liveDays === 1 ? '' : 's'}`;
}

/** "14 completed visit" / "14 completed visits". */
export function completedVisitsLabel(count: number): string {
  return `${count} completed visit${count === 1 ? '' : 's'}`;
}

/** "1 appointment" / "6 appointments". */
export function appointmentsLabel(count: number): string {
  return `${COUNT.format(count)} appointment${count === 1 ? '' : 's'}`;
}

/**
 * An honest "share of a base" sub-line for a KPI tile: "5 of 6 sign-ups", plus " · 83%" only on a base of
 * 20+. When the count outgrew its base (they come from different reads — e.g. visits not tracked), it just
 * names the base ("4 visitors tracked") instead of printing "6 of 4". null when there is no base yet.
 */
export function shareOfBaseLine(count: number, base: number, noun: string, outgrownNote = noun): string | null {
  if (base <= 0) return null;
  if (count > base) return `${COUNT.format(base)} ${outgrownNote}`;
  const share = safeShare(count, base);
  return share.pct === null ? `${share.text} ${noun}` : `${share.text} ${noun} · ${share.pct}%`;
}

/**
 * Funnel step rate for a chip, as a 0..1 ratio: null on a base under 20 (too small for a percentage) and
 * when the step outgrew its base (different reads), so no chip ever shows a misleading or >100% figure.
 */
export function guardedStepRate(count: number, base: number): number | null {
  const share = safeShare(count, base);
  if (share.pct === null || count > base) return null;
  return share.pct / 100;
}

/** A 0..1 ratio as a whole percent ("46%"). */
export function formatWholePercent(rate: number): string {
  return `${Math.round(rate * 100)}%`;
}
